import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, message, Popconfirm } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import {
    type RemoveUserOnRoleMutation,
    type RemoveUserOnRoleMutationVariables,
    RemoveUserOnRoleDocument,
} from '../../../api/mutations/removeUserOnRole';
import hasPermissions from '../../../utilities/hasPermissions';

export type RoleUserListActionsProps = {
    role: RoleSpecsFragment;
    user: RoleSpecsFragment['users'][number];
};

const RoleUserListActions = ({ user, role }: RoleUserListActionsProps) => {
    const { t } = useTranslation(['roleDetails']);

    const apolloClient = useApolloClient();
    const userId = user.id;
    const roleId = role.id;

    const onDelete = useCallback(async () => {
        message.loading({
            content: t('roleDetails:deleteUser.messages.submitting'),
            key: 'primary',
            duration: 0,
        });

        await apolloClient
            .mutate<RemoveUserOnRoleMutation, RemoveUserOnRoleMutationVariables>({
                mutation: RemoveUserOnRoleDocument,
                variables: { roleId, userId },
            })
            .finally(() => {
                message.destroy('primary');
            });

        message.success({
            content: t('roleDetails:deleteUser.messages.success'),
            key: 'primary',
        });
    }, [t, apolloClient, userId, roleId]);

    if (!hasPermissions(role.rolePermissions, [permissionKind.updateRole])) {
        return null;
    }

    return (
        <Popconfirm
            cancelText={t('roleDetails:deleteUser.cancelText')}
            icon={<WarningOutlined style={{ color: 'red' }} />}
            okText={t('roleDetails:deleteUser.okText')}
            onConfirm={onDelete}
            placement="left"
            title={t('roleDetails:deleteUser.message')}
        >
            <Button icon={<DeleteOutlined />} type="link" />
        </Popconfirm>
    );
};

export default RoleUserListActions;
